import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import AuthLayout, { AuthLink } from "@/components/AuthLayout";

export default function SignupPage() {
  const [form, setForm] = useState({
    company_name: "",
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm_password) {
      setError("Passwords do not match");
      return;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const data = await register({
        company_name: form.company_name,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        password: form.password,
      });
      // New flow: registrations are gated by admin approval. Backend returns
      // `status: "pending_approval"` without a token — show the "you're in the
      // queue" screen instead of auto-logging in.
      if (data?.status === "pending_approval") {
        setPendingApproval({ email: form.email, name: form.first_name });
      } else {
        navigate("/publisher");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const labelCls = "text-xs uppercase tracking-[0.2em] text-slate-500 font-bold";

  // Pending-approval confirmation screen — shown after successful register
  // so the pub knows they're in the queue and aren't stuck wondering why
  // their new credentials don't log in.
  if (pendingApproval) {
    return (
      <AuthLayout
        eyebrow="Access / Registration Received"
        title="You&rsquo;re in the queue"
        subtitle=""
      >
        <div className="space-y-5" data-testid="signup-pending-approval">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-4">
            <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-amber-700 mb-1">
              Pending admin approval
            </div>
            <p className="text-[14px] text-amber-900 leading-relaxed">
              Thanks{pendingApproval.name ? `, ${pendingApproval.name}` : ""} — we&rsquo;ve received your sign-up for
              <b> {pendingApproval.email}</b>. An admin will review your account shortly and activate it.
              You&rsquo;ll get a confirmation email the moment you&rsquo;re approved.
            </p>
          </div>
          <p className="text-[12px] text-slate-500">
            You won&rsquo;t be able to log in until your account is activated. Typical review time is under one business day.
          </p>
          <Button
            onClick={() => navigate("/login")}
            className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white h-10"
            data-testid="signup-pending-back-btn"
          >
            Back to Sign In
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      eyebrow="Access / Publisher Signup"
      title="Create your account"
      subtitle="Get approved access to brands, tracking links, and transparent payouts."
      footer={
        <p className="text-sm text-slate-500 text-center">
          Already have an account?{" "}
          <AuthLink to="/login" data-testid="login-link">
            Sign In
          </AuthLink>
        </p>
      }
    >
      <div data-testid="signup-page" />
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div
            className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
            data-testid="signup-error"
          >
            {error}
          </div>
        )}

        <div className="space-y-1.5">
          <Label className={labelCls}>Company Name</Label>
          <Input
            name="company_name"
            value={form.company_name}
            onChange={handleChange}
            required
            className="h-10"
            data-testid="signup-company-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className={labelCls}>First Name</Label>
            <Input
              name="first_name"
              value={form.first_name}
              onChange={handleChange}
              required
              className="h-10"
              data-testid="signup-firstname-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Last Name</Label>
            <Input
              name="last_name"
              value={form.last_name}
              onChange={handleChange}
              required
              className="h-10"
              data-testid="signup-lastname-input"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className={labelCls}>Email</Label>
          <Input
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
            className="h-10"
            data-testid="signup-email-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className={labelCls}>Password</Label>
            <Input
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              required
              className="h-10"
              data-testid="signup-password-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Confirm Password</Label>
            <Input
              name="confirm_password"
              type="password"
              value={form.confirm_password}
              onChange={handleChange}
              required
              className="h-10"
              data-testid="signup-confirm-input"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full h-11 bg-[#002FA7] hover:bg-[#00227A] text-white font-medium tracking-tight mt-2"
          data-testid="signup-submit-btn"
        >
          {loading ? "Creating Account…" : "Create Account"}
        </Button>
      </form>
    </AuthLayout>
  );
}
